import { useState, useCallback, useRef } from 'react'
import { Link } from 'react-router'
import { Heading } from '@/components/Heading'
import { Box } from '@/components/Box'
import { Button } from '@/components/Button'
import { ApplyidLogo } from '@/components/ApplyidLogo'
import { UploadIcon } from '@/components/UploadIcon'
import { DocumentIcon } from '@heroicons/react/24/outline'
import { TrashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid'
import { PageLoader } from '@/components/PageLoader.tsx'
import { Header } from '@/components/HeaderBusiness'
import useFormContext from '@/context/useFormContext'
import { useFormSubmission } from '@/hooks/useFormSubmission'
import usePersistedNavigation from '@/hooks/usePersistedNavigation'
import { ROUTES } from '@/config/routes'
import ReCAPTCHA, { ReCAPTCHAHandle } from '@steamer-academy/react-google-recaptcha'
import { apiUpload } from '@/services/apiService'
import { validateUploadFile } from '@/utils/fileUpload'
import { useGTMTracking } from '@/hooks/useGTMTracking'

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  file: File;
}

function ApplicationOptions() {
  const navigate = usePersistedNavigation()
  const { state } = useFormContext()
  const { 
    trackBusinessSubmitApplication, 
    trackBusinessClickApplication,
    trackBusinessUploadUsed,
    trackBusinessUploadErrorSize,
    trackBusinessUploadErrorType,
    trackBusinessUploadErrorJpeg
  } = useGTMTracking()
  const [applyid, setApplyid] = useState(false)
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [isRecaptchaError, setIsRecaptchaError] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHAHandle>(null)
  const [isPostCallChecked, setIsPostCallChecked] = useState(false)
  const [isPostCallError, setIsPostCallError] = useState(false)

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApplyid(event.target.checked)
    // Track ApplyID click for business application
    if (event.target.checked) {
      trackBusinessClickApplication()
    }
  }

  const { handleFormSubmit, isLoading } = useFormSubmission({
    onSuccess: async (response) => {
      setRecaptchaToken(null)

      // Once the application is submitted, we need to upload the files
      setIsUploading(true)

      const pendingFiles = files.filter(f => f.status === 'pending')
      const results = []

      for (const file of pendingFiles) {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'uploading' } : f
        ))
        
        const success = await uploadFile(file)
        results.push({ file, success })
      }

      setIsUploading(false)

      // Get applyid success from response
      const applyidResponseSuccess = response?.data?.primaryApplicantAplyIdStatus?.isSuccess === true

      // Navigate to end application page
      navigate(ROUTES.BUSINESS.END_APPLICATION.path, { state: { files: results, isCheckedAplyID: applyid, applyidResponseSuccess } })
    },
    onError: async (error) => {
      setRecaptchaToken(null)
      recaptchaRef.current?.reset()
      window.alert('There was an error submitting the application. Please try again if the problem persists, or contact us on 0800 500 832.')
      console.error('Error during form submission:', error)
    }
  })

  const validateFile = (file: File): { isValid: boolean; error?: string } => {
    const result = validateUploadFile(file)

    if (!result.isValid) {
      if (result.reason === 'size') {
        trackBusinessUploadErrorSize()
      } else if (result.reason === 'jpeg') {
        trackBusinessUploadErrorJpeg()
      } else {
        trackBusinessUploadErrorType()
      }
    }

    return result
  }

  const handleFileUpload = useCallback(async (file: File) => {
    const validation = validateFile(file)
    
    const newFile: UploadedFile = {
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      status: validation.isValid ? 'pending' : 'error',
      error: validation.error,
      file: file
    }

    // Track successful file upload
    if (validation.isValid) {
      trackBusinessUploadUsed()
    }

    setFiles(prev => [...prev, newFile])
  }, [trackBusinessUploadUsed])

  const uploadFile = async (file: UploadedFile): Promise<boolean> => {
    try {
      // Check if debug mode is enabled and this is the first file
      const isDebugMode = localStorage.getItem('debug') === 'true'
      const isFirstFile = files.findIndex(f => f.id === file.id) === 0
      
      if (isDebugMode && isFirstFile) {
        throw new Error('Debug mode: Intentionally failing first file upload')
      }

      const formData = new FormData()
      formData.append('file', file.file)

      const uploadResponse = await apiUpload(`Form/UploadDocument?formId=${state.formId}`, formData)

      if (!uploadResponse.success) {
        throw new Error('Upload failed')
      }

      setFiles(prev => prev.map(f => 
        f.id === file.id ? { ...f, status: 'success' } : f
      ))
      return true
    } catch (error) {
      setFiles(prev => prev.map(f => 
        f.id === file.id ? { ...f, status: 'error', error: 'Upload failed' } : f
      ))
      return false
    }
  }

  const handleSubmit = async () => {
    // If isPostCall is true, require checkbox for submit
    if (state.isPostCall && !isPostCallChecked) {
      setIsPostCallError(true)
      return
    }
    setIsPostCallError(false)

    if (!recaptchaToken) {
      setIsRecaptchaError(true)
      return
    }

    // Track business form submission
    if (applyid) {
      trackBusinessSubmitApplication()
    }

    // Clone aboutYourBusiness and apply the null logic
    const aboutYourBusiness = { ...state.business.aboutYourBusiness }
    if (aboutYourBusiness.businessType === 'SOLE') {
      aboutYourBusiness.limitedCompany = null
    } else if (aboutYourBusiness.businessType === 'LIMITED') {
      aboutYourBusiness.soleTrader = null
    }

    const data = {
      ...state,
      personal: null,
      business: {
        ...state.business,
        aboutYourBusiness, // updated with null logic
        applicationOptions: {
          isCheckedAplyID: applyid
        },
        financialPosition: {
          ...state.business.financialPosition,
          additionalNotes: [
            state.business.financialPosition.additionalNotes,
            applyid ? '\n The main applicant has applied for ApplyID. ' : ''
          ].filter(Boolean).join('')
        }
      }
    }

    handleFormSubmit(data, recaptchaToken)
  }

  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const droppedFiles = Array.from(e.dataTransfer.files)
    droppedFiles.forEach(file => {
      handleFileUpload(file)
    })
  }, [handleFileUpload])

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    selectedFiles.forEach(file => {
      handleFileUpload(file)
    })
  }, [handleFileUpload])

  const handleRemoveFile = useCallback((fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId))
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  return (
    <div>
      <Header />
      <section className='pb-4'>
        <Heading>To speed up your application you can choose to also complete these <span className='font-bold'>optional</span> steps now:</Heading>
        <div className='px-8 max-w-5xl mx-auto space-y-4 text-center'>
          <div className='mx-auto w-full max-w-[365px] text-lg'>
            <p className='pb-1' id='applyidVerificationDescription'>Start <span className='font-bold'>ID verification</span> using <span className='sr-only'>APLYid</span></p>
            <label className='transition duration-300 cursor-pointer w-full flex items-center gap-5 text-black rounded-[4px] mb-2 p-8 border border-white bg-white has-[:checked]:border-[var(--theme-text)] has-[:checked]:text-white has-[:checked]:bg-[var(--primary-button-bg-hover)]'>
              <div className='group grid size-7 grid-cols-1'>
                <input
                  id='applyidVerification'
                  name='applyidVerification'
                  type='checkbox'
                  aria-describedby='applyidVerificationDescription'
                  onChange={handleCheckboxChange}
                  className='
                    col-start-1 row-start-1 cursor-pointer appearance-none rounded-[4px] border border-[var(--theme-text)]
                    bg-white checked:bg-white indeterminate:border-[var(--checkbox-active-bg)] indeterminate:bg-[var(--checkbox-active-bg)]
                    focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--checkbox-active-bg)]
                    disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto size-7'
                />
                <svg
                  fill='none'
                  viewBox='0 0 14 14'
                  className='pointer-events-none col-start-1 row-start-1 size-6 self-center justify-self-center stroke-[var(--brand-1)] group-has-[:disabled]:stroke-gray-950/25'
                >
                  <path
                    d='M3 8L6 11L11 3.5'
                    strokeWidth={1.5}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='opacity-0 group-has-[:checked]:opacity-100'
                  />
                  <path
                    d='M3 7H11'
                    strokeWidth={1}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='opacity-0 group-has-[:indeterminate]:opacity-100'
                  />
                </svg>
              </div>
              <ApplyidLogo />
            </label>
            <p>We use APLYiD to verify your identity via your mobile phone</p>
            <Link className='underline text-[var(--theme-link-text)]' to='https://www.udc.co.nz/apply_moreinfo' target='_blank'>More information about APLYiD</Link>
          </div>
        </div>
      </section>
      <section className='pb-12'>
        <Heading as='h2'>You can also choose to upload the following documents as part of your application:</Heading>
        <Box className='max-md:pt-9 sm:pb-10 sm:max-w-4xl'>
          <div className='grid md:grid-cols-3 gap-8 md:-mx-2 -mt-3 mb-0'>
            <div className='text-base'>
              <h4 className='font-bold text-base mb-3'>Documents you can upload</h4>
              <p>
                Proof of address:<br />
                (one of the following)
              </p>
              <ul className='text-sm leading-[1.55] list-disc pt-1 pl-6 mb-6 last:md:pb-8 last:max-md:mb-0'>
                <li className=''>
                  Utility bill
                </li>
                <li className=''>
                  Bank statement
                </li>
                <li className=''>
                  Rates bill
                </li>
                <li className=''>
                  Insurance policy
                </li>
                <li className=''>
                  IRD tax notification/Certificate
                </li>
              </ul>
              <p className={`${applyid ? 'text-[var(--upload-list-disabled-text)]' : '' }`}>
                <span className={`${applyid ? 'line-through' : '' }`}>Primary ID documents:</span><br />
                (Not required if using AplyID)
              </p>
              <ul className={`${applyid ? 'line-through text-[var(--upload-list-disabled-text)]' : '' } text-sm leading-[1.55] list-disc pt-1 pl-6 mb-6 last:md:pb-8 last:max-md:mb-0`}>
                <li className=''>
                  Passport
                </li>
                <li className=''>
                  Drivers licence
                </li>
              </ul>
            </div>
            <div className='col-span-2'>
              <div className='flex flex-col h-full'>
                <h4 className='text-[28px] pb-2.5'>Upload documents</h4>
                <div 
                  className='flex flex-col gap-1 items-center justify-center border border-dashed border-[var(--upload-border)] max-md:h-[350px] text-[var(--upload-text)] bg-[var(--upload-bg)] grow rounded-md text-center text-lg'
                  onDrop={handleFileDrop}
                  onDragOver={handleDragOver}
                >
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    multiple
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileInput}
                  />
                  <label htmlFor="file-upload" className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                    <UploadIcon />
                    <p>
                      Drag and drop your files or <span className='underline text-[var(--theme-link-text)]'>browse</span><br />
                      Max file size: <span className='font-bold'>20mb</span><br />
                      Supported file types: <span className='font-bold'>.jpg, .jpeg, .png, .pdf</span>
                    </p>
                  </label>
                </div>
              </div>
            </div>
          </div>
          {files.length > 0 && (
            <div className='grid grid-cols-1 gap-5 lg:gap-8 border-t border-[var(--form-box-divider)] pt-8 mt-8 -mb-7 md:-mx-2 lg:pb-4'>
              {files.map(file => (
                <div 
                  key={file.id}
                  className={`flex items-center justify-between gap-6 px-5 py-4 rounded-[4px] ${
                    file.status === 'error' 
                      ? 'bg-[var(--theme-danger-bg)] text-[var(--theme-danger)]' 
                      : 'text-[var(--theme-neutral)] border-1 border-[var(--theme-neutral)]/30'
                  } text-sm/5`}
                >
                  {file.status === 'error' ? (
                    <XCircleIcon aria-hidden={true} className='size-6 shrink-0' />
                  ) : file.status === 'success' ? (
                    <CheckCircleIcon aria-hidden={true} className='size-6 shrink-0' />
                  ) : null}
                  <div className='flex items-center gap-6 grow pr-5'>
                    <DocumentIcon aria-hidden={true} className='size-5 stroke-2 shrink-0' />
                    <div>
                      <p>
                        {file.name}<br />
                        <span className='font-bold'>{(file.size / (1024 * 1024)).toFixed(2)}mb</span>
                      </p>
                      {file.error && <p>{file.error}</p>}
                      {file.status === 'uploading' && <p>Uploading...</p>}
                      {file.status === 'success' && <p>File successfully uploaded.</p>}
                    </div>
                  </div>
                  <button 
                    className='inline-flex items-center gap-0.5 text-sm/5 whitespace-nowrap cursor-pointer'
                    onClick={() => handleRemoveFile(file.id)}
                  >
                    <TrashIcon aria-hidden={true} className='size-5 -mt-0.5' /> 
                    <span className='underline'>Remove file</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </Box>
        
        {/* Insert confirmation checkbox if isPostCall is true */}
        {state.isPostCall && (
          <div className="my-8 max-w-2xl mx-auto bg-white border border-gray-300 rounded-lg shadow-sm p-6 flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <input
                id="post-call-confirm-checkbox"
                type="checkbox"
                className="mt-1 accent-[var(--brand-1)] w-5 h-5 border-gray-400 rounded focus:ring-2 focus:ring-[var(--brand-1)]"
                checked={isPostCallChecked}
                onChange={e => setIsPostCallChecked(e.target.checked)}
              />
              <div className="text-sm text-gray-700">
                <span className="font-semibold block mb-2">By ticking this box, you confirm that:</span>
                <ul className="list-disc pl-5 space-y-2">
                  <li>You have checked all information automatically entered into the application and updated where necessary.</li>
                  <li>You are not currently bankrupt or have a history of credit defaults.</li>
                  <li>You confirm the UDC Privacy Statement applies to your application.</li>
                  <li>Your application is subject to UDC's credit approval process. Lending criteria applied.</li>
                </ul>
              </div>
            </div>
            {isPostCallError && (
              <p className="text-red-600 text-sm font-medium mt-1">You must confirm the above before submitting your application.</p>
            )}
          </div>
        )}

        <div className='flex flex-col justify-center items-center py-10 pb-12 max-w-5xl mx-auto'>
          <div className={`inline-flex justify-center rounded-md border p-0.5 ${ isRecaptchaError ? 'border-[var(--const-error)]' : 'border-[transparent]'}`} style={{opacity: isLoading ? 0 : 1}}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6LdAhscUAAAAAGNb6RwOaBoOh7TWi50a0-iwg-sE"
              onChange={(token: string | null) => {
                setRecaptchaToken(token)
                setIsRecaptchaError(false)
              }}
              onExpired={() => setRecaptchaToken(null)}
              onErrored={() => {
                window.alert('We are sorry, there was an error with the reCAPTCHA. Please try again.')
                setRecaptchaToken(null)
                recaptchaRef.current?.reset()
              }}
            />
          </div>
          <p className={`text-sm text-[var(--const-error)] text-center pt-4 ${ isRecaptchaError ? 'block' : 'hidden'}`}>Please verify that you are human by clicking the reCAPTCHA checkbox</p>
        </div>
        
        <div className='flex justify-between mt-8 pb-12 max-w-2xl mx-auto'>
          <Button iconPosition='left' onClick={() => navigate(ROUTES.BUSINESS.FINANCIAL_POSITION.path)}>Back</Button>
          <Button onClick={handleSubmit}>Submit</Button>
        </div>
      </section>
      <PageLoader open={isLoading} />
      <PageLoader open={isUploading}>
        Uploading<br /> your files
        <span className='block text-base text-center mt-2'>File {files.filter(f => f.status !== 'pending').length}/{files.length}</span>
      </PageLoader>
    </div>
  )
}

export default ApplicationOptions
